import { useStore } from '@nanostores/react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogActions, DialogContent, DialogHeader } from '~/components/ui/Dialog';
import { Input } from '~/components/ui/Input';
import { createOrganization } from '~/lib/.client/api/organizations';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { teamStore } from '~/lib/stores/team';
import { getGithubConnectAppRoute } from '~/lib/urls';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { OrganizationProviderType } from '~/types/organization';
import { classNames } from '~/utils/classNames';

interface ProviderBlockProps {
  provider: OrganizationProviderType;
  selectedProvider: OrganizationProviderType;
  setSelectedProvider: (selectedProvider: OrganizationProviderType) => void;
  title: string;
  description: string;
  icon: string;
  children?: ReactNode;
}

const ProviderBlock = ({
  selectedProvider,
  setSelectedProvider,
  provider,
  children,
  title,
  description,
  icon,
}: ProviderBlockProps) => {
  const handleProviderSelect = () => {
    const previousProvider = selectedProvider;
    setSelectedProvider(provider);

    // track TeamProviderSelected when provider changes
    if (previousProvider !== provider) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamProviderSelected,
          properties: {
            provider_type: provider,
            previous_provider: previousProvider,
          },
        },
      });
    }
  };

  return (
    <div
      className={classNames(
        'flex items-start w-full p-5 gap-1 sm:gap-5 border border-bolt-ds-borderOutline rounded-lg',
        selectedProvider === provider ? 'border-bolt-ds-brandHighlight' : '',
      )}
      onClick={handleProviderSelect}
    >
      <div className={classNames(icon, 'shrink-0 hidden sm:block w-14 h-14 text-bolt-ds-textPrimary')} />
      <div className="flex flex-col gap-2 w-full mr-1">
        <h2 className="text-base font-semibold text-bolt-ds-textPrimary">{title}</h2>
        <p className="text-bolt-ds-textSecondary text-sm">{description}</p>
        {children}
      </div>

      <div
        className={classNames(
          'max-[400px]:hidden block w-6 h-6 p-1',
          selectedProvider === provider ? 'flex items-center justify-center border border-accent-500 rounded-full' : '',
        )}
      >
        {selectedProvider === provider && <div className="i-ph:check text-accent-500" />}
      </div>
    </div>
  );
};

export const CreateTeamModal = () => {
  const createTeamSlugData = useStore(teamStore.createTeamSlugData);
  const [selectedProvider, setSelectedProvider] = useState<'github' | 'custom'>('custom');
  const [slug, setSlug] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [setupStartTime, setSetupStartTime] = useState<number | null>(null);

  // track TeamSetupStarted when modal opens
  useEffect(() => {
    if (createTeamSlugData && !setupStartTime) {
      const startTime = Date.now();
      setSetupStartTime(startTime);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamSetupStarted,
          properties: {
            selected_provider: 'custom',
            initial_plan_level: createTeamSlugData.level,
            initial_interval: createTeamSlugData.interval,
          },
        },
      });
    }
  }, [createTeamSlugData, setupStartTime]);

  const onClose = () => {
    // track TeamSignupAbandoned if user closes without completing
    if (createTeamSlugData && setupStartTime) {
      const timeSpentSeconds = Math.round((Date.now() - setupStartTime) / 1000);

      const lastCompletedStep = slug
        ? 'team_slug_entered'
        : selectedProvider !== 'custom'
          ? 'provider_selected'
          : 'setup_started';

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamSignupAbandoned,
          properties: {
            last_completed_step: lastCompletedStep,
            time_spent_seconds: timeSpentSeconds,
            plan_level: createTeamSlugData.level,
            selected_provider: selectedProvider,
            had_slug: !!slug,
          },
        },
      });
    }

    teamStore.createTeamSlugData.set(null);
  };

  const handleCreateBoltTeam = async () => {
    if (!createTeamSlugData) {
      return;
    }

    setIsCreating(true);

    const result = await createOrganization({
      slug,
      setupState: { level: createTeamSlugData.level, interval: createTeamSlugData.interval },
    });

    setIsCreating(false);

    if ('error' in result) {
      toast.error(result.error);
    } else if (createTeamSlugData) {
      // track TeamCreated on successful organization creation
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamCreated,
          properties: {
            provider_type: 'custom',
            plan_level: createTeamSlugData.level,
            interval: createTeamSlugData.interval,
            team_slug: slug,
            organization_id: result.organization.id,
          },
        },
      });

      teamStore.createTeamData.set({
        organization: result.organization,
        level: createTeamSlugData?.level,
        interval: createTeamSlugData?.interval,
        members: [],
      });
      onClose();
    }
  };

  return (
    <Dialog open={!!createTeamSlugData} onClose={onClose} maxWidth={680} closeOnEscape={false}>
      <DialogHeader title="New Team" />

      <DialogContent>
        <div className="flex flex-col gap-2">
          <ProviderBlock
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            provider="custom"
            title="Create a new Bolt team"
            icon="i-bolt:logos-bolt-b"
            description="Invite new members, delete and manage your team directly from Bolt."
          >
            <div className="flex gap-x-6 gap-y-2 flex-wrap items-center">
              <span
                className={classNames(
                  'text-bolt-ds-textPrimary font-semibold text-sm',
                  selectedProvider !== 'custom' ? 'opacity-50' : '',
                )}
              >
                Team name*
              </span>
              <Input
                id="slug"
                value={slug}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateBoltTeam();
                  }
                }}
                placeholder="Enter a unique team name..."
                required={selectedProvider === 'custom'}
                className={classNames('grow-1 w-45 sm:w-auto', selectedProvider !== 'custom' ? 'opacity-50' : '')}
                inputClassName="text-sm"
                onChange={(e) => {
                  const rawValue = e.target.value;

                  const sanitized = rawValue
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-_]/g, '');

                  setSlug(sanitized);

                  // track TeamSlugEntered when user types
                  if (sanitized.length > 0 && createTeamSlugData) {
                    sendAnalyticsEvent({
                      action: AnalyticsAction.Track,
                      payload: {
                        event: AnalyticsTrackEvent.TeamSlugEntered,
                        properties: {
                          slug_length: sanitized.length,
                          contains_special_chars: /[^a-zA-Z0-9-_]/.test(sanitized),
                          plan_level: createTeamSlugData.level,
                        },
                      },
                    });
                  }
                }}
              />
            </div>
          </ProviderBlock>

          <ProviderBlock
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            provider="github"
            title="Connect GitHub Organization"
            icon="i-bolt:logos-github?mask"
            description="Sync members of your GitHub Organization to a new Bolt Team."
          />
        </div>
      </DialogContent>

      <DialogActions className="border-px border-bolt-elements-borderColor border-b">
        {selectedProvider === 'github' && createTeamSlugData ? (
          <Button asChild>
            <a
              href={getGithubConnectAppRoute({
                level: createTeamSlugData.level,
                interval: createTeamSlugData.interval,
                addTeamFlow: true,
              })}
            >
              Install GitHub App
            </a>
          </Button>
        ) : (
          <Button
            disabled={isCreating}
            onClick={handleCreateBoltTeam}
            startIcon={isCreating ? 'i-svg-spinners:90-ring-with-bg scale-115' : undefined}
          >
            {isCreating ? 'Creating...' : 'Save and continue'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
